import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { authService } from "../services/auth.api";
import { validateRegister } from "../utils/auth.validation";
import type { SignUpData } from "../types";
import { usePhoneCheck } from "../../../shared/hooks/usePhoneCheck";
import { useEmailCheck } from "../../../shared/hooks/useEmailCheck";
import { extractFieldErrors } from "../../../api/api";
import { useLanguage } from "../../../context/LanguageContext";

interface RegisterFormData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  phone_country: string;
  whatsapp_number: string;
  password: string;
  confirmPassword: string;
  [key: string]: any;
}

interface UseRegisterOptions {
  role: "client" | "talent";
  redirectTo?: string;
}

export const useRegister = ({ role, redirectTo = "/login" }: UseRegisterOptions) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [formData, setFormData] = useState<RegisterFormData>({ first_name: "" , last_name: "", email: "", phone: "+20", phone_country: "EG", whatsapp_number: "", password: "", confirmPassword: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const { phoneExists, checkingPhone } = usePhoneCheck(formData.phone);
  const { emailExists, checkingEmail } = useEmailCheck(formData.email);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError("");
    if (fieldErrors[name]) setFieldErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const setField = (name: string, value: any) =>
    setFormData((prev) => ({ ...prev, [name]: value }));

  const handleSubmit = async (e: React.FormEvent, additionalData?: Record<string, any>) => {
    e.preventDefault();
    const errors = validateRegister(formData, t);
    if (Object.keys(errors).length) { setFieldErrors(errors); return; }
    if (checkingPhone) { setError(t("shared.phoneField.checking")); return; }
    if (phoneExists) { setError(t("shared.phoneField.alreadyRegistered")); return; }
    if (checkingEmail) { setError(t("auth.checkingEmail")); return; }
    if (emailExists) { setError(t("auth.emailAlreadyRegistered")); return; }

    setIsLoading(true);
    setError("");
    try {
      await authService.signUp({
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        phone_country: formData.phone_country,
        whatsapp_number: formData.whatsapp_number,
        password: formData.password,
        password_confirmation: formData.confirmPassword,
        role,
        ...additionalData,
      } as SignUpData & Record<string, any>);
      navigate(redirectTo, { state: { message: t("auth.validation.accountCreatedRedirect") } });
    } catch (err: any) {
      const backendFieldErrors = extractFieldErrors(err);
      if (Object.keys(backendFieldErrors).length) {
        setFieldErrors((prev) => ({ ...prev, ...backendFieldErrors }));
      } else {
        setError(err?.message || t("auth.validation.createAccountFailed"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return { formData, isLoading, error, fieldErrors, phoneExists, checkingPhone, emailExists, checkingEmail, handleChange, handleSubmit, setField, setFormData };
};
